// A role's rate, as one string.
//
// hiring_listings.compensation_details is free text, and owners filled it the
// way you'd expect free text to be filled: "250 wk", "$500 weekly", "900",
// "ask". The board then printed whatever came out. These helpers let the form
// collect an amount and a period as separate controls and compose the one
// canonical string the column still stores — no migration, old rows keep
// working — while parse() reads an existing value back into those controls so
// editing a listing doesn't silently reformat what its owner wrote.

export const RATE_PERIODS = [
  { value: "day", label: "per day", short: "day" },
  { value: "wk", label: "per week", short: "wk" },
  { value: "2wks", label: "every 2 weeks", short: "2 wks" },
  { value: "mo", label: "per month", short: "mo" },
] as const

export type RatePeriod = (typeof RATE_PERIODS)[number]["value"]

export interface ParsedRate {
  amount: string
  period: RatePeriod | ""
  /** Anything the owner wrote that isn't the amount or the period. */
  note: string
}

const PERIOD_WORDS: Array<[RegExp, RatePeriod]> = [
  [/\b(2\s*w(ee)?ks?|biweekly|bi-weekly|every\s*2\s*weeks?)\b/i, "2wks"],
  [/\b(w|wk|wks|week|weekly|weeks)\b/i, "wk"],
  [/\b(mo|mos|month|monthly|months)\b/i, "mo"],
  [/\b(d|day|daily|days)\b/i, "day"],
]

/** "$250/wk · keeps 100%" → { amount: "250", period: "wk", note: "keeps 100%" } */
export function parseRate(raw: string | null | undefined): ParsedRate {
  const value = (raw ?? "").trim()
  if (!value) return { amount: "", period: "", note: "" }

  // A composed string keeps its note after the separator; free text has none.
  const [head, ...rest] = value.split("·")
  let note = rest.join("·").trim()

  const amountMatch = head.match(/\$?\s*([\d,]+(?:\.\d{1,2})?)/)
  const amount = amountMatch ? amountMatch[1].replace(/,/g, "") : ""

  let period: RatePeriod | "" = ""
  for (const [pattern, p] of PERIOD_WORDS) {
    if (pattern.test(head)) { period = p; break }
  }

  // Nothing recognisable: keep every character as the note rather than drop
  // what the owner wrote. "Ask for rates" survives an edit untouched.
  if (!amount && !period) return { amount: "", period: "", note: value }

  // A head with leftovers past the amount and period ("500 weekly + booth fee")
  // folds those leftovers into the note.
  const leftover = head
    .replace(/\$?\s*[\d,]+(?:\.\d{1,2})?/, "")
    .replace(PERIOD_WORDS.find(([, p]) => p === period)?.[0] ?? /$^/, "")
    .replace(/^[\s/|,+–—-]+|[\s/|,+–—-]+$/g, "")
    .trim()
  if (leftover) note = note ? `${leftover} · ${note}` : leftover

  return { amount, period, note }
}

/** The inverse: what gets stored, and what the board prints. */
export function formatRate({ amount, period, note }: ParsedRate): string {
  const clean = amount.replace(/[^\d.]/g, "")
  const head = clean
    ? `$${Number(clean).toLocaleString("en-US")}${period ? `/${RATE_PERIODS.find(p => p.value === period)?.short}` : ""}`
    : ""
  const tail = note.trim()
  if (head && tail) return `${head} · ${tail}`
  return head || tail
}
